import {
  Button,
  ButtonMode,
  Chip,
  colors,
  Icon,
  ICONS,
  Modal,
  ModalWidth,
  Tooltip,
  TooltipPosition,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useState } from 'react';
import { useGlobalModalContext } from '../../../components/GlobalModal';
import { DocketCase } from '../../../models/CompanyDockets';
import { DATE_FORMATS, formatDate } from '../../../utils/dateUtils';
import styles from './ViewDocketCaseItem.module.scss';

const cx = classNames.bind(styles);

interface ViewDocketCasesModalProps {
  docketCases: DocketCase[];
  selectedDocketCase: DocketCase;
  onConfirm: (docketCase) => void;
}

const ViewDocketCasesModal = ({
  docketCases,
  selectedDocketCase,
  onConfirm,
}: ViewDocketCasesModalProps): React.ReactElement => {
  const { closeModal } = useGlobalModalContext();
  const [checked, setChecked] = useState<DocketCase>(selectedDocketCase);

  const renderActions = () => {
    return (
      <>
        <Button mode={ButtonMode.SECONDARY} onClick={closeModal}>
          Cancel
        </Button>
        <Button mode={ButtonMode.PRIMARY} onClick={() => onConfirm(checked)} dataTestId="confirm-view-case">
          View Case
        </Button>
      </>
    );
  };

  const renderDocketCase = (docketCase: DocketCase) => {
    const { defaultCase, name, caseNumber, judges, date, id } = docketCase;
    const judgesString = judges.join(', ');
    const showCheckmark = false;
    return (
      <div className={cx('list-item')} key={id} data-testId="docket-case">
        <input
          type="radio"
          name="case"
          id={String(id)}
          checked={checked.id === id}
          onChange={() => {
            setChecked(docketCase);
          }}
        />
        <label htmlFor={String(id)} tabIndex={-1} className={cx('columns')} data-testid="docket-case-label">
          <div className={cx('column')}>
            {showCheckmark && <Icon name={ICONS.Checkmark} color={colors.primary800} width={14} height={10} />}
          </div>
          <div className={cx('column')}>
            <div className={cx('main-info')}>
              <div className={cx('first-line')}>
                {defaultCase && (
                  <Chip
                    label="Key Case"
                    color={colors['docket-keycase-font-color']}
                    backgroundColor={colors['docket-keycase-bg-color']}
                    dataTestId="key-case"
                  />
                )}
                <Tooltip position={TooltipPosition.TopCenter} content={name}>
                  <div className={cx('name')}>{name}</div>
                </Tooltip>
              </div>
              <div className={cx('number')}>{caseNumber}</div>
            </div>
          </div>
          <div className={cx('column')}>
            <Tooltip position={TooltipPosition.TopCenter} content={judgesString} disabled={!judgesString}>
              <div className={cx('user-name')}>{judgesString}</div>
            </Tooltip>
          </div>
          <div className={cx('column', 'date')}>{formatDate(date, DATE_FORMATS.MONTH_ABBR_DATE, '-')}</div>
        </label>
      </div>
    );
  };

  return (
    <Modal
      width={ModalWidth.Large}
      open
      onClose={closeModal}
      headerTitle="View Related Cases"
      footer={renderActions()}
      dataTestId="related-cases-modal"
    >
      <div className={cx('body')}>
        <div className={cx('header')}>
          <div className={cx('column')} />
          <div className={cx('column')}>Case</div>
          <div className={cx('column')}>Judge</div>
          <div className={cx('column')}>Date Filed</div>
        </div>
        <div className={cx('list')}>{docketCases.map(renderDocketCase)}</div>
      </div>
    </Modal>
  );
};

export default ViewDocketCasesModal;
